<script setup>
import { inject, ref } from 'vue';
import { TabPanel } from '@headlessui/vue';
import { ArrowLongRightIcon } from '@heroicons/vue/24/outline';
import { UserIcon } from '@heroicons/vue/20/solid';
import ContributorModal from './ContributorModal.vue';

const programPackage = inject('programPackage');

const displayedContributor = ref(null);
</script>

<template>
    <TabPanel class="focus-visible:outline-deep-teal-700">
        <h2 class="text-2xl font-semibold text-deep-teal-700 pb-4">
            {{ $t('eventDetails.tabs.contributors') }}
        </h2>
        <ul
            v-if="programPackage.contributions"
            role="list"
            class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-x-12 md:gap-y-12 divide-y md:divide-none divide-gray-200 -mt-6 md:mt-0"
        >
            <li
                v-for="(contributor, index) in programPackage.contributions"
                :key="index"
                class="flex py-6 md:py-0"
            >
                <button
                    class="flex items-center space-x-6 outline-none group"
                    @click="displayedContributor = contributor"
                >
                    <picture
                        v-if="contributor.profile.image"
                        class="shrink-0"
                    >
                        <source
                            :srcset="contributor.profile.image.glide_avatar_image_url_avif"
                            type="image/avif"
                        >
                        <source
                            :srcset="contributor.profile.image.glide_avatar_image_url_webp"
                            type="image/webp"
                        >
                        <img
                            class="h-20 lg:h-28 xl:h-36 w-20 lg:w-28 xl:w-36 object-cover rounded-full"
                            :src="contributor.profile.image.glide_avatar_image_url_jpg"
                            :alt="`${$t('eventDetails.contributor.image.alt')} ${contributor.name}`"
                            loading="lazy"
                        >
                    </picture>
                    <div
                        v-else
                        class="flex items-center justify-center shrink-0 h-20 lg:h-28 xl:h-36 w-20 lg:w-28 xl:w-36 bg-white rounded-full border"
                        aria-hidden="true"
                    >
                        <UserIcon class="h-12 lg:h-16 xl:h-20 w-12 lg:w-16 xl:w-20 text-gray-600" />
                    </div>

                    <div class="text-left space-y-1.5 md:space-y-3">
                        <div class="space-y-0.5 md:space-y-1">
                            <div
                                class="font-semibold text-lg text-deep-teal-700 leading-tight"
                            >
                                {{ contributor.profile.display_name }}
                            </div>

                            <div
                                v-if="contributor.profile.company"
                                class="font-light text-sm/tight text-gray-700"
                            >
                                {{ contributor.profile.company }}
                            </div>
                        </div>

                        <button
                            type="button"
                            tabindex="-1"
                            class="inline-flex items-center text-left text-deep-teal-700 group-hover:text-deep-teal-900 group-focus:text-deep-teal-900 group-hover:underline group-focus:underline space-x-1.5"
                        >
                            <span>
                                {{ $t('eventDetails.actions.showContributorModal') }}
                            </span>

                            <ArrowLongRightIcon
                                class="h-6 w-6"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </button>
            </li>
        </ul>
    </TabPanel>

    <ContributorModal
        v-if="displayedContributor"
        :contributor="displayedContributor"
        :show="Boolean(displayedContributor)"
        @closed="displayedContributor = null"
    />
</template>
